/**
 * The door onto a trial, decided in one place: Home and My Enrolments both ask this module
 * whether a trial opens and where, so the two pages cannot drift apart.
 */

/**
 * Cohort statuses after which a participation is history. Mirrors candidate-portal-backend's
 * `TERMINAL_CYCLE_STATUSES` in `src/utils/work_trial_utils.py`.
 */
export const PAST_CYCLE_STATUSES = Object.freeze(['ended', 'cancelled', 'archived']);

/**
 * Whether a cohort is over, so its participation is listed as a past trial.
 * @param {Object} cycle - Projected cycle
 * @returns {boolean}
 */
export const isPastCycle = (cycle) => PAST_CYCLE_STATUSES.includes(cycle?.status);

/**
 * Enrolment statuses that still hold a spot once the cohort is running. Mirrors appli-jobs'
 * `LIVE_ENROLMENT_STATUSES` in `src/ts/cycle_start.py`: a manual-start cohort never opens a
 * confirmation window, so nobody on it could have confirmed, and the engine seats every live
 * enrolment and hands it a trial while its status stays `enrolled`.
 */
export const LIVE_ENROLMENT_STATUSES = Object.freeze(['enrolled', 'confirmed']);

/**
 * Whether a cohort's own page opens for the candidate. Mirrors candidate-portal-backend's rule for
 * `GET /test-cycles/<id>`: an open cycle is shown to everyone, a later one only to the people who
 * still hold a place on it, and anyone else gets a 404. A `no_show` or a `cancelled_credited`
 * enrolment holds no place, and a cohort that is over is kept as a record, never as a link.
 * @param {Object} enrolment - The candidate's enrolment on the cohort
 * @param {Object} cycle - Projected cycle
 * @returns {boolean}
 */
export const canOpenCycle = (enrolment, cycle) => !isPastCycle(cycle)
    && (cycle?.status === 'open' || LIVE_ENROLMENT_STATUSES.includes(enrolment?.status));

/**
 * Whether the candidate may open a cohort's trial: a live enrolment that was given a trial, on a
 * running cohort. Every other cohort status, including one added later, has no door, and so does
 * every enrolment status that has lost the spot — `no_show`, `cancelled_credited`.
 * @param {Object} enrolment - The candidate's enrolment on the cohort
 * @param {Object} cycle - Projected cycle
 * @returns {boolean}
 */
export const canOpenTrial = (enrolment, cycle) => cycle?.status === 'running'
    && LIVE_ENROLMENT_STATUSES.includes(enrolment?.status)
    && Boolean(enrolment?.work_trial_id);

/**
 * Where a trial opens: the per-trial engine switch between the workspace and the legacy page.
 * @param {Object} trial - Carries `work_trial_id`, and `eval_engine` when known
 * @returns {string}
 */
export const trialHref = (trial) => (
    trial.eval_engine === 'eval4'
        ? `/workspace/${trial.work_trial_id}`
        : `/work-trial/${trial.work_trial_id}`
);
